import { groupBy, keyBy, mapValues } from "lodash"
import moment from "moment"

import ActionTypes from "../../enums/ActionTypes"
import { initialState } from "./initialState"
import { setConflictsByOrderPosition, setOperationConflictsByStatus, getResourceConflicts } from "./helpers/conflicts"
import { addOwner } from "./helpers/addOwner"

const filterOperationsByDateRange = (operations, filterDateRange) => {
  if (!operations || !operations.length) {
    return operations || []
  }

  const fromDate = filterDateRange?.fromDate
  const toDate = filterDateRange?.toDate

  if (!fromDate && !toDate) {
    return operations
  }

  const parseDateTime = date => {
    const parsedDate = moment(date)
    return parsedDate.isValid() ? parsedDate.valueOf() : NaN
  }

  const fromDateTime = fromDate ? parseDateTime(fromDate) : NaN
  const toDateTime = toDate ? parseDateTime(toDate) : NaN
  if (Number.isNaN(fromDateTime) && Number.isNaN(toDateTime)) {
    return operations
  }

  const filteredOperations = []
  for (let index = 0; index < operations.length; index += 1) {
    const operation = operations[index]
    const startDateTime = parseDateTime(operation.startDate)
    const endDateTime = parseDateTime(operation.endDate)

    if (Number.isNaN(startDateTime) || Number.isNaN(endDateTime)) {
      filteredOperations.push(operation)
      continue
    }
    if (!Number.isNaN(toDateTime) && startDateTime > toDateTime) {
      continue
    }
    if (!Number.isNaN(fromDateTime) && endDateTime < fromDateTime) {
      continue
    }
    filteredOperations.push(operation)
  }

  return filteredOperations
}

const readIntoStore = (payload, filterDateRange) => {
  const {
    activities,
    articles,
    availabilityPlans,
    customers,
    operations,
    orderPositions,
    productionOrders,
    projects,
    publicHolidays,
    resourceGroups,
    resources,
    users,
    valueAdds,
  } = payload

  const filteredOperations = filterOperationsByDateRange(operations, filterDateRange)
  const resourcesGrouped = keyBy(resources, "resourceId")
  const state = {
    activities: keyBy(activities, "activityId"),
    articles: keyBy(articles, "articleId"),
    capacityPlans: keyBy(availabilityPlans, "availabilityPlanId"),
    customers: keyBy(customers, "customerId"),
    holidays: publicHolidays,
    operations: keyBy(operations, "operationId"),
    orderPositions: keyBy(orderPositions, "orderPositionId"),
    productionOrders: keyBy(productionOrders, "productionOrderId"),
    projects: keyBy(projects, "projectId"),
    resourceGroups: keyBy(resourceGroups, "resourceGroupId"),
    resources: resourcesGrouped,
    valueAdds: keyBy(valueAdds, "valueAddId"),
    conflicts: {
      datesMissedByPosition: {},
      operationsDelayed: {},
      operationsByResource: mapValues(groupBy(filteredOperations, "resource"), (itemsWithStringDates, resource) =>
        getResourceConflicts("operationId", itemsWithStringDates, resourcesGrouped[resource])
      ),
    },
  }

  setConflictsByOrderPosition(state)
  setOperationConflictsByStatus(state)
  state.projects = addOwner(state.projects, users)
  state.orderPositions = addOwner(state.orderPositions, users)
  state.productionOrders = addOwner(state.productionOrders, users)

  return state
}

export default (state = initialState, action, filterDateRange) => {
  const refetch = action?.payload?.refetch
  switch (action.type) {
    case ActionTypes.TIMELINE_DATA_FETCH_FAILURE: {
      return {
        ...state,
        status: {
          isPending: false,
          isLoaded: false,
          isRefetching: false,
        },
      }
    }
    case ActionTypes.TIMELINE_DATA_FETCH_REQUEST: {
      const fetchStatus = refetch ? "isRefetching" : "isPending"
      return {
        ...state,
        status: {
          ...state.status,
          [fetchStatus]: true,
        },
      }
    }
    case ActionTypes.TIMELINE_DATA_FETCH_SUCCESS: {
      return {
        ...state,
        ...readIntoStore(action.payload, filterDateRange),
        status: {
          ...state.status,
          isPending: false,
          isLoaded: true,
          isRefetching: false,
        },
      }
    }

    case ActionTypes.TIMELINE_DATA_FETCH_RESET: {
      return {
        ...state,
        status: {
          isPending: false,
          isLoaded: false,
          isRefetching: false,
        },
      }
    }

    default:
      return state
  }
}
